import React, { useState } from "react";
import { FiUser, FiCopy, FiCheck } from "react-icons/fi";
import { BsRobot } from "react-icons/bs";
import { ChatMessage } from "api/ai";

interface ChatMessageItemProps {
  message: ChatMessage;
}

export const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const isBot = message.sender === "bot";

  const handleCopy = () => {
    navigator.clipboard.writeText(message.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Process plain text and render formatted line breaks and bullet points cleanly
  const renderFormattedText = (text: string) => {
    const lines = text.split("\n");
    return lines.map((line, index) => {
      // Bold text handling (*text* or **text**)
      const parts = line.split(/(\*\*.*?\*\*|\*.*?\*)/g);
      const formattedLine = parts.map((part, pIdx) => {
        if (
          (part.startsWith("**") && part.endsWith("**")) ||
          (part.startsWith("*") && part.endsWith("*"))
        ) {
          const cleanText = part.replace(/^\*+|\*+$/g, "");
          return (
            <strong key={pIdx} className="font-semibold text-textPrimary dark:text-white">
              {cleanText}
            </strong>
          );
        }
        return part;
      });

      // Check if bullet point
      if (line.trim().startsWith("* ") || line.trim().startsWith("- ")) {
        return (
          <div key={index} className="ml-3 my-0.5 flex items-start gap-2">
            <span className="text-brand-500 font-bold">•</span>
            <span>{formattedLine}</span>
          </div>
        );
      }

      return (
        <React.Fragment key={index}>
          {formattedLine}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      className={`flex w-full gap-2.5 my-2.5 ${
        isBot ? "justify-start" : "justify-end"
      }`}
    >
      {/* Bot Icon */}
      {isBot && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white shadow-sm dark:bg-brand-400">
          <BsRobot className="h-4 w-4" />
        </div>
      )}

      {/* Message Bubble Container */}
      <div
        className={`group relative max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed transition-all ${
          isBot
            ? "rounded-tl-none border border-borderPrimary bg-surfaceSecondary text-textPrimary shadow-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            : "rounded-tr-none bg-brand-500 text-white shadow-md dark:bg-brand-500"
        }`}
      >
        <div className="whitespace-pre-wrap">{renderFormattedText(message.text)}</div>

        {/* Footer info & Copy button */}
        <div
          className={`mt-1.5 flex items-center justify-between gap-2 text-[11px] ${
            isBot ? "text-textMuted dark:text-gray-400" : "text-brand-100"
          }`}
        >
          <span>{message.timestamp}</span>

          {isBot && (
            <button
              onClick={handleCopy}
              title="Copy response"
              className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-brand-500 dark:hover:text-white flex items-center gap-1"
            >
              {copied ? (
                <>
                  <FiCheck className="h-3 w-3 text-green-500" />
                  <span className="text-green-500 font-medium">Copied</span>
                </>
              ) : (
                <>
                  <FiCopy className="h-3 w-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* User Icon */}
      {!isBot && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-navy-600 dark:text-white">
          <FiUser className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

export const TypingIndicator: React.FC = () => {
  return (
    <div className="flex w-full gap-2.5 my-2.5 justify-start items-center">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white shadow-sm">
        <BsRobot className="h-4 w-4 animate-pulse" />
      </div>
      <div className="rounded-2xl rounded-tl-none border border-borderPrimary bg-surfaceSecondary px-4 py-3 text-sm text-textSecondary dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium mr-1">AI Assistant is thinking</span>
          <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: "0ms" }}></span>
          <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: "150ms" }}></span>
          <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: "300ms" }}></span>
        </div>
      </div>
    </div>
  );
};
